import React, {
	useCallback,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import {
	Button,
	Input,
	Label,
	Modal,
	ModalBody,
	ModalFooter,
	ModalHeader,
	Spinner,
} from "reactstrap";
import { toast } from "react-toastify";
import { analyseMonitoringPrompt } from "../../../services/monitoringApi";
import {
	CartesianGrid,
	Line,
	LineChart,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts";
import {
	ChevronDown,
	Microscope,
	Search,
	Settings,
	User,
	UserRound,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import MonitoringSparkline from "./MonitoringSparkline";
import {
	MoveToTopicDropdown,
	MonitoringSelect,
} from "./Keywords/KeywordsDashboard";
import {
	adaptPromptDetail,
	adaptPromptsList,
	adaptTopicsPayload,
	buildPromptTopicGroups,
	engineLabel,
	getPromptLatestRunLabel,
	intentLabel,
	isPromptGrouped,
	journeyLabel,
	variantRowFromParent,
	getPromptRunLifecycleContext,
	shouldToggleNoPersonaRun,
} from "./monitoringAdapters";
import PromptPersonasModal from "./PromptPersonasModal";
import {
	countStartedPromptLines,
	parseCsvPrompts,
	parsePromptLines,
	PROMPT_BULK_MAX_LINES,
	PROMPT_BULK_MIN_LEN,
} from "./monitoringPromptBulkImport";
import {
	formatGroundedSource,
	preprocessResponse,
	sourceToDomain,
} from "../Report/citationGridUtils";
import { ProviderIcon, ProviderChip } from "../shared/ProviderIcon";
import {
	addMonitoringPrompt,
	autoAssignMonitoringPrompts,
	deleteMonitoringTopic,
	getMonitoringPromptDetail,
	getMonitoringPrompts,
	getMonitoringTopics,
	mergeMonitoringTopics,
	moveMonitoringPrompts,
	reactivateMonitoringPrompt,
	renameMonitoringTopic,
	retireMonitoringPrompt,
	setMonitoringPromptNoPersona,
	splitMonitoringTopic,
	updateMonitoringPrompt,
	listMonitoringWritingAssistantPersonas,
} from "../../../services/monitoringApi";

const RAW_ANSWER_MARKDOWN_COMPONENTS = {
	a: ({ href, children }) => (
		<a href={href} target="_blank" rel="noopener noreferrer">
			{children}
		</a>
	),
	strong: ({ children }) => <strong>{children}</strong>,
	em: ({ children }) => <em>{children}</em>,
	ul: ({ children }) => <ul>{children}</ul>,
	ol: ({ children }) => <ol>{children}</ol>,
	li: ({ children }) => <li>{children}</li>,
	code: ({ children }) => <code>{children}</code>,
	blockquote: ({ children }) => <blockquote>{children}</blockquote>,
	p: ({ children }) => <p>{children}</p>,
	h1: ({ children }) => <h4>{children}</h4>,
	h2: ({ children }) => <h4>{children}</h4>,
	h3: ({ children }) => <h4>{children}</h4>,
};

function getRawAnswerText(answer = {}) {
	return (
		answer.full_response ||
		answer.full_response_snippet ||
		answer.fullResponse ||
		answer.answer ||
		answer.answer_text ||
		answer.answerText ||
		answer.response_snippet ||
		answer.responseSnippet ||
		answer.answer_excerpt ||
		answer.answerExcerpt ||
		""
	);
}

function extractUrlsFromSourcesFooter(footer) {
	const items = [];
	const seen = new Set();
	const push = (url, showFullUrl) => {
		const cleaned = String(url || "").trim();
		if (!cleaned) return;
		const key = cleaned.toLowerCase();
		if (seen.has(key)) return;
		seen.add(key);
		items.push({ url: cleaned, showFullUrl: Boolean(showFullUrl) });
	};

	const urlRe = /https?:\/\/[^\s<>)\]*"']+/gi;
	let urlMatch;
	while ((urlMatch = urlRe.exec(footer)) !== null) {
		const cleaned = urlMatch[0].replace(/[.,;:!?)]+$/g, "");
		// ChatGPT/OpenAI footers: keep full URL visible in Sources list
		push(cleaned, true);
	}
	// Gemini-style: [Grounded: domain.com] lines with no full URL
	const groundedRe = /\[Grounded:\s*([^\]]+)\]/gi;
	let groundedMatch;
	while ((groundedMatch = groundedRe.exec(footer)) !== null) {
		const domain = groundedMatch[1].trim();
		if (!domain) continue;
		if (/^https?:\/\//i.test(domain)) {
			push(domain, shouldShowFullSourceUrl(domain));
		} else {
			push(`https://${domain.replace(/^\/+/, "")}`, false);
		}
	}
	return items;
}

/** True for OpenAI-style citation URLs (path/query), not bare homepage domains. */
function shouldShowFullSourceUrl(url) {
	const raw = String(url || "").trim();
	if (!/^https?:\/\//i.test(raw)) return false;
	if (/[?&]utm_source=openai\b/i.test(raw)) return true;
	try {
		const parsed = new URL(raw);
		if (parsed.search && parsed.search.length > 1) return true;
		const path = parsed.pathname || "/";
		return path !== "/" && path !== "";
	} catch {
		return false;
	}
}

/**
 * Engines often append a trailing SOURCES / Citations footer (full URLs or
 * [Grounded: domain] lines). Split it out of the answer body so Sources render
 * in the structured list below.
 */
function splitInlineSourcesFromAnswer(text) {
	const raw = String(text || "").replace(/\r\n/g, "\n");
	if (!raw.trim()) return { body: "", urls: [] };

	const headerRe =
		/^[ \t]*(?:\*\*|__|#+\s*)?(?:SOURCES?|Citations?)(?:\*\*|__)?\s*:?[ \t]*$/gim;
	let lastHeader = null;
	let match;
	while ((match = headerRe.exec(raw)) !== null) {
		lastHeader = match;
	}
	if (!lastHeader) {
		return { body: raw.trim(), urls: [] };
	}

	const body = raw.slice(0, lastHeader.index).trimEnd();
	const footer = raw.slice(lastHeader.index + lastHeader[0].length);
	const urls = extractUrlsFromSourcesFooter(footer);

	const footerLines = footer
		.split("\n")
		.map((line) => line.trim())
		.filter(Boolean);
	const looksLikeSourcesFooter = footerLines.some(
		(line) =>
			/https?:\/\//i.test(line) || /\[Grounded:\s*[^\]]+\]/i.test(line)
	);
	// Avoid stripping a mid-answer "Sources" heading that isn't a citation footer
	if (!looksLikeSourcesFooter && urls.length === 0) {
		return { body: raw.trim(), urls: [] };
	}

	return {
		body: body.trim(),
		urls,
	};
}

function extractSourceDomain(urlOrLabel) {
	const raw = String(urlOrLabel || "").trim();
	if (!raw) return "";
	const groundedBracket = raw.match(/^\[Grounded:\s*(.+?)\]$/i);
	if (groundedBracket) return groundedBracket[1].trim();
	const groundedPlain = raw.match(/^Grounded:\s*(.+)$/i);
	if (groundedPlain) return groundedPlain[1].trim();
	return sourceToDomain(raw);
}

function sourceHref(urlOrLabel) {
	const raw = String(urlOrLabel || "").trim();
	if (/^https?:\/\//i.test(raw)) return raw;
	return null;
}

/** Normalize monitoring PromptDetail sources: `{ url, ours }` | string. */
function normalizeAnswerSources(answer = {}, extraUrls = []) {
	const candidates = [];
	if (Array.isArray(answer.sources) && answer.sources.length) {
		candidates.push(...answer.sources);
	} else {
		const own = answer.own_sources || answer.ownSources || [];
		const competitor =
			answer.competitor_sources || answer.competitorSources || [];
		const other =
			answer.cited_urls ||
			answer.citedUrls ||
			answer.grounding_sources ||
			answer.groundingSources ||
			answer.urls ||
			[];
		(Array.isArray(own) ? own : []).forEach((s) =>
			candidates.push(typeof s === "object" ? s : { url: s, ours: true })
		);
		(Array.isArray(competitor) ? competitor : []).forEach((s) =>
			candidates.push(typeof s === "object" ? s : { url: s, ours: false })
		);
		(Array.isArray(other) ? other : []).forEach((s) =>
			candidates.push(typeof s === "object" ? s : { url: s, ours: false })
		);
	}
	(Array.isArray(extraUrls) ? extraUrls : []).forEach((item) => {
		if (!item) return;
		if (typeof item === "string") {
			candidates.push({
				url: item,
				ours: false,
				showFullUrl: shouldShowFullSourceUrl(item),
			});
			return;
		}
		if (typeof item === "object" && item.url) {
			candidates.push({
				...item,
				ours: Boolean(item.ours),
				showFullUrl:
					item.showFullUrl === true || shouldShowFullSourceUrl(item.url),
			});
		}
	});

	const seen = new Map();
	const out = [];
	candidates.forEach((item) => {
		let url = "";
		let ours = false;
		let showFullUrl = false;
		if (typeof item === "string") {
			url = item.trim();
			showFullUrl = shouldShowFullSourceUrl(url);
		} else if (item && typeof item === "object") {
			url = String(item.url || item.href || item.source || "").trim();
			ours = Boolean(item.ours ?? item.is_ours ?? item.isOurs);
			showFullUrl =
				item.showFullUrl === true || shouldShowFullSourceUrl(url);
		}
		if (!url) return;
		const domain = extractSourceDomain(url);
		if (!domain) return;
		const key = domain.toLowerCase();
		const existingIdx = seen.get(key);
		const next = {
			url,
			ours,
			domain,
			href: sourceHref(url),
			showFullUrl,
		};
		if (existingIdx == null) {
			seen.set(key, out.length);
			out.push(next);
			return;
		}
		// Prefer a full OpenAI-style URL over a domain-only / Grounded entry
		const existing = out[existingIdx];
		if (showFullUrl && !existing.showFullUrl) {
			out[existingIdx] = {
				...next,
				ours: existing.ours || ours,
			};
		} else if (ours && !existing.ours) {
			out[existingIdx] = { ...existing, ours: true };
		}
	});
	return out;
}

function answerStatusMeta(answer = {}) {
	if (answer.cited) {
		return {
			label:
				answer.position != null && answer.position !== ""
					? `Cited · #${answer.position}`
					: "Cited",
			tone: "positive",
		};
	}
	if (answer.mentioned) {
		return { label: "Mentioned", tone: "info" };
	}
	return { label: "Not cited", tone: "neutral" };
}

const DURATIONS = [
	{ value: 7, label: "7 days" },
	{ value: 30, label: "30 days" },
	{ value: 90, label: "90 days" },
	{ value: 0, label: "All time" },
];

// Order matches Add prompt UI reference
const INTENT_OPTIONS = [
	{ value: "", label: "Auto-detect" },
	{ value: "informational", label: "Informational" },
	{ value: "commercial", label: "Commercial" },
	{ value: "transactional", label: "Transactional" },
	{ value: "navigational", label: "Navigational" },
	{ value: "local", label: "Local" },
];

const JOURNEY_OPTIONS = [
	{ value: "", label: "Auto-detect" },
	{ value: "awareness", label: "Awareness" },
	{ value: "consideration", label: "Consideration" },
	{ value: "decision", label: "Decision" },
];

const EDIT_INTENT_OPTIONS = [
	{ value: "", label: " - " },
	{ value: "informational", label: "Informational" },
	{ value: "commercial", label: "Commercial" },
	{ value: "transactional", label: "Transactional" },
	{ value: "navigational", label: "Navigational" },
	{ value: "local", label: "Local" },
];

const EDIT_JOURNEY_OPTIONS = [
	{ value: "", label: " - " },
	{ value: "awareness", label: "Awareness" },
	{ value: "consideration", label: "Consideration" },
	{ value: "decision", label: "Decision" },
];

const STATUS_TABS = [
	{ value: "active", label: "Active" },
	{ value: "retired", label: "Retired" },
	{ value: "all", label: "All" },
];

const TOPIC_AUTO = "";
const TOPIC_NEW = "__new__";

function renderNarrative(text, lossPhrase) {
	if (!lossPhrase || !text.includes(lossPhrase)) return text;
	const parts = text.split(lossPhrase);
	return (
		<>
			{parts[0]}
			<span className="monitoring__ember-text">{lossPhrase}</span>
			{parts.slice(1).join(lossPhrase)}
		</>
	);
}

function IntentChip({ intent }) {
	if (!intent) return null;
	return (
		<span className={`monitoring__intent-chip monitoring__intent-chip--${intent}`}>
			{intentLabel(intent)}
		</span>
	);
}

function JourneyChip({ stage }) {
	if (!stage) return null;
	return (
		<span
			className={`monitoring__journey-chip monitoring__journey-chip--${stage}`}
		>
			{journeyLabel(stage)}
		</span>
	);
}

function SourcePill({ sourceKey, label }) {
	return (
		<span className={`monitoring__source-badge monitoring__source-badge--${sourceKey}`}>
			{label}
		</span>
	);
}

function RawAnswersModal({ websiteId, promptId, promptText, onClose }) {
	const [detail, setDetail] = useState(null);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		let cancelled = false;
		async function load() {
			setLoading(true);
			const res = await getMonitoringPromptDetail(websiteId, promptId, 0);
			if (cancelled) return;
			if (res.ok) setDetail(res.detail);
			setLoading(false);
		}
		load();
		return () => {
			cancelled = true;
		};
	}, [websiteId, promptId]);

	const answers = detail?.latest_answers || [];
	const latestRunLabel = getPromptLatestRunLabel(detail);

	return (
		<div
			className="monitoring__modal-backdrop"
			role="dialog"
			aria-modal="true"
			aria-label="Raw engine answers"
			onClick={onClose}
		>
			<div
				className="monitoring__modal monitoring__modal--answers"
				onClick={(e) => e.stopPropagation()}
			>
				<div className="monitoring__modal-header">
					<div>
						<h3 className="monitoring__modal-title mb-0">Raw engine answers</h3>
						{promptText && (
							<p className="monitoring__modal-sub text-muted mb-0">{promptText}</p>
						)}
						{!loading && latestRunLabel && (
							<p className="monitoring__modal-latest-run text-muted mb-0">
								Latest run · {latestRunLabel}
							</p>
						)}
					</div>
					<button type="button" className="monitoring__action-btn" onClick={onClose}>
						Close
					</button>
				</div>
				{loading ? (
					<p className="text-muted mb-0">Loading…</p>
				) : answers.length === 0 ? (
					<p className="text-muted mb-0">No completed run yet for this prompt.</p>
				) : (
					<div className="monitoring__answers-list">
						{answers.map((a) => {
							const split = splitInlineSourcesFromAnswer(getRawAnswerText(a));
							const answerText = split.body;
							const sources = normalizeAnswerSources(a, split.urls);
							const status = answerStatusMeta(a);
							const ownCount = sources.filter((s) => s.ours).length;
							return (
								<article key={a.engine} className="monitoring__answer-card">
									<div className="monitoring__answer-head">
										<div className="monitoring__answer-engine">
											<ProviderIcon provider={a.engine} size={18} />
											<strong>{engineLabel(a.engine) || a.engine}</strong>
											<span
												className={`monitoring__sentiment-badge monitoring__sentiment-badge--${status.tone}`}
											>
												{status.label}
											</span>
										</div>
										<div className="monitoring__answer-head-meta">
											<span className="monitoring__answer-meta-pill">
												{sources.length} source
												{sources.length === 1 ? "" : "s"}
											</span>
											{ownCount > 0 ? (
												<span className="monitoring__answer-meta-pill monitoring__answer-meta-pill--ours">
													{ownCount} ours
												</span>
											) : null}
										</div>
									</div>
									{answerText ? (
										<div className="monitoring__answer-body">
											<ReactMarkdown
												components={RAW_ANSWER_MARKDOWN_COMPONENTS}
											>
												{preprocessResponse(answerText)}
											</ReactMarkdown>
										</div>
									) : (
										<p className="monitoring__answer-body monitoring__answer-body--empty mb-0">
											No answer text captured.
										</p>
									)}
									<div className="monitoring__answer-sources-block">
										<p className="monitoring__answer-sources-title mb-0">
											Sources
										</p>
										{sources.length > 0 ? (
											<ul className="monitoring__answer-sources mb-0">
												{sources.map((source) => {
													const label = source.showFullUrl
														? source.href || source.url
														: formatGroundedSource(source.domain);
													const linkClass = [
														"monitoring__answer-source-link",
														source.ours ? "is-ours" : "",
														source.showFullUrl
															? "monitoring__answer-source-link--full-url"
															: "",
													]
														.filter(Boolean)
														.join(" ");
													return (
														<li key={`${source.domain}-${source.url}`}>
															{source.href ? (
																<a
																	className={linkClass}
																	href={source.href}
																	target="_blank"
																	rel="noopener noreferrer"
																	title={source.url}
																>
																	{label}
																</a>
															) : (
																<span
																	className={linkClass}
																	title={source.url}
																>
																	{label}
																</span>
															)}
														</li>
													);
												})}
											</ul>
										) : (
											<p className="monitoring__answer-sources-empty mb-0">
												No sources cited
											</p>
										)}
									</div>
								</article>
							);
						})}
					</div>
				)}
			</div>
		</div>
	);
}

function TrendDrilldown({ websiteId, promptId, declining, onViewRaw }) {
	const [days, setDays] = useState(90);
	const [detail, setDetail] = useState(null);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		let cancelled = false;
		async function load() {
			setLoading(true);
			const res = await getMonitoringPromptDetail(websiteId, promptId, days);
			if (cancelled) return;
			if (res.ok) setDetail(adaptPromptDetail(res.detail));
			else setDetail(null);
			setLoading(false);
		}
		load();
		return () => {
			cancelled = true;
		};
	}, [websiteId, promptId, days]);

	if (loading) return <p className="text-muted mb-0">Loading trend…</p>;
	if (!detail) return <p className="text-muted mb-0">Could not load prompt detail.</p>;

	const yMax = Math.max(
		1,
		...(detail.series || []).map((p) => p.count),
		(detail.engineHistory || []).length || 1
	);

	return (
		<div className="monitoring__prompt-detail">
			<p className="monitoring__prompt-detail-label mb-2">
				Run-over-run  -  engines citing this prompt
			</p>
			<div className="monitoring__prompt-detail-grid">
				<div>
					<div className="d-flex justify-content-end mb-1">
						<select
							className="monitoring__filter-select"
							value={days}
							onChange={(e) => setDays(Number(e.target.value))}
							aria-label="Trend duration"
						>
							{DURATIONS.map((d) => (
								<option key={d.value} value={d.value}>
									{d.label}
								</option>
							))}
						</select>
					</div>
					{detail.series?.length ? (
						<div className="monitoring__chart-wrap monitoring__chart-wrap--xs">
							<ResponsiveContainer width="100%" height={120}>
								<LineChart
									data={detail.series}
									margin={{ top: 4, right: 8, left: 0, bottom: 0 }}
								>
									<CartesianGrid
										strokeDasharray="3 3"
										stroke="#E5E7EB"
										vertical={false}
									/>
									<XAxis
										dataKey="date"
										tick={{ fontSize: 11, fill: "#6B7280" }}
										axisLine={{ stroke: "#E5E7EB" }}
										tickLine={false}
									/>
									<YAxis
										domain={[0, yMax]}
										tick={{ fontSize: 11, fill: "#6B7280" }}
										axisLine={false}
										tickLine={false}
										width={24}
									/>
									<Tooltip
										contentStyle={{
											borderRadius: 8,
											border: "1px solid #E5E7EB",
											fontSize: 12,
										}}
									/>
									<Line
										type="monotone"
										dataKey="count"
										name="Engines"
										stroke={declining ? "#EA580C" : "#4F46E5"}
										strokeWidth={2}
										dot={{ r: 3 }}
									/>
								</LineChart>
							</ResponsiveContainer>
						</div>
					) : (
						<p className="text-muted mb-0">No completed runs in this window.</p>
					)}
				</div>
				<div>
					<p className="monitoring__prompt-narrative mb-2">
						{renderNarrative(detail.narrative, detail.lossPhrase)}
					</p>
					<button type="button" className="monitoring__text-link" onClick={onViewRaw}>
						View raw engine answers →
					</button>
				</div>
			</div>
		</div>
	);
}

function AddPromptModal({
	isOpen,
	onClose,
	onSubmitOne,
	onCompleted,
	onBusyChange,
	topics = [],
	websiteId,
	maxPersonas = 0,
	examplePrompt,
}) {
	const [promptText, setPromptText] = useState("");
	const [topicChoice, setTopicChoice] = useState("");
	const [newTopicName, setNewTopicName] = useState("");
	const [intent, setIntent] = useState("");
	const [journeyStage, setJourneyStage] = useState("");
	const [forceBranded, setForceBranded] = useState(false);
	const [noPersona, setNoPersona] = useState(true);
	const [personaIds, setPersonaIds] = useState([]);
	const [personas, setPersonas] = useState([]);
	const [personasLoading, setPersonasLoading] = useState(false);
	const [submitBusy, setSubmitBusy] = useState(false);
	const [progress, setProgress] = useState(null);
	const [info, setInfo] = useState(null);
	const [submitError, setSubmitError] = useState(null);
	const [failures, setFailures] = useState([]);
	const textareaRef = useRef(null);
	const csvFileRef = useRef(null);

	const startedCount = countStartedPromptLines(promptText);
	const overLimit = startedCount > PROMPT_BULK_MAX_LINES;
	const validLines = parsePromptLines(promptText).filter(
		(line) => line.length >= PROMPT_BULK_MIN_LEN
	);
	const busy = submitBusy;

	useEffect(() => {
		onBusyChange?.(isOpen && submitBusy);
	}, [isOpen, submitBusy, onBusyChange]);

	useLayoutEffect(() => {
		if (!isOpen) {
			setPersonas([]);
			setPersonasLoading(false);
			return;
		}
		setPersonas([]);
		setPersonasLoading(true);
	}, [isOpen]);

	useEffect(() => {
		if (!isOpen) {
			setPromptText("");
			setTopicChoice("");
			setNewTopicName("");
			setIntent("");
			setJourneyStage("");
			setForceBranded(false);
			setNoPersona(true);
			setPersonaIds([]);
			setSubmitBusy(false);
			setProgress(null);
			setInfo(null);
			setSubmitError(null);
			setFailures([]);
			return;
		}
		const t = window.setTimeout(() => textareaRef.current?.focus?.(), 50);
		let cancelled = false;
		listMonitoringWritingAssistantPersonas(websiteId).then((res) => {
			if (cancelled) return;
			setPersonasLoading(false);
			setPersonas(res.ok ? res.personas || [] : []);
		});
		return () => {
			cancelled = true;
			window.clearTimeout(t);
		};
	}, [isOpen, websiteId]);

	const topicOptions = useMemo(
		() => [
			{ value: TOPIC_AUTO, label: "Auto-detect (recommended)" },
			...topics.map((t) => ({
				value: String(t.topicId),
				label: t.name,
			})),
			{ value: TOPIC_NEW, label: "Create new topic..." },
		],
		[topics]
	);

	const placeholderExample =
		examplePrompt?.trim() || "best running shoes for flat feet";

	function toggleAddPersona(id) {
		const num = Number(id);
		setPersonaIds((prev) => {
			let next;
			if (prev.includes(num)) {
				next = prev.filter((x) => x !== num);
			} else if (maxPersonas > 0 && prev.length >= maxPersonas) {
				return prev;
			} else {
				next = [...prev, num];
			}
			if (next.length === 0) setNoPersona(true);
			else setNoPersona(false);
			return next;
		});
	}

	function buildSharedPayload() {
		const payload = {
			intent: intent || undefined,
			journeyStage: journeyStage || undefined,
		};
		if (forceBranded) payload.isBranded = true;
		if (personaIds.length) payload.personaIds = personaIds;
		if (!noPersona) payload.runWithoutPersona = false;
		if (topicChoice === TOPIC_NEW) {
			payload.autoTopic = false;
			payload.createTopicName = newTopicName.trim();
		} else if (topicChoice) {
			payload.autoTopic = false;
			payload.topicId = Number(topicChoice);
		} else {
			payload.autoTopic = true;
		}
		return payload;
	}

	function onCsvFile(file) {
		if (!file) return;
		setSubmitError(null);
		setInfo(null);
		setFailures([]);
		const reader = new FileReader();
		reader.onload = () => {
			const { prompts, truncated } = parseCsvPrompts(String(reader.result ?? ""));
			if (prompts.length === 0) {
				setSubmitError("CSV has no prompts. Use a prompts column (see sample CSV).");
				return;
			}
			setPromptText(prompts.join("\n"));
			setInfo(
				truncated
					? `Loaded first ${PROMPT_BULK_MAX_LINES} prompts from CSV (max ${PROMPT_BULK_MAX_LINES} per add).`
					: `Loaded ${prompts.length} prompt${prompts.length === 1 ? "" : "s"} from CSV.`
			);
		};
		reader.onerror = () => setSubmitError("Could not read CSV file");
		reader.readAsText(file);
		if (csvFileRef.current) csvFileRef.current.value = "";
	}

	async function handleSubmit() {
		if (busy || overLimit) return;
		setSubmitError(null);
		setFailures([]);
		if (topicChoice === TOPIC_NEW && !newTopicName.trim()) {
			setSubmitError("Enter a name for the new topic, or pick another option");
			return;
		}
		if (!noPersona && personaIds.length === 0) {
			setSubmitError("Choose at least one: the no-persona run or a persona");
			return;
		}

		const lines = parsePromptLines(promptText);
		if (lines.length === 0) {
			setSubmitError("Enter a prompt of at least 4 characters");
			return;
		}
		if (lines.some((line) => line.length < PROMPT_BULK_MIN_LEN)) {
			setSubmitError("Each prompt line must be at least 4 characters");
			return;
		}
		if (lines.length > PROMPT_BULK_MAX_LINES) {
			setSubmitError(
				`Maximum ${PROMPT_BULK_MAX_LINES} prompts at once (${lines.length}/${PROMPT_BULK_MAX_LINES})`
			);
			return;
		}

		const shared = buildSharedPayload();
		setSubmitBusy(true);
		setInfo(null);
		setProgress({ k: 0, n: lines.length });

		const created = [];
		const failed = [];

		for (let i = 0; i < lines.length; i += 1) {
			setProgress({ k: i + 1, n: lines.length });
			// eslint-disable-next-line no-await-in-loop
			const res = await onSubmitOne?.({
				...shared,
				promptText: lines[i],
			});
			if (res?.ok) {
				created.push(lines[i]);
			} else {
				failed.push({
					text: lines[i],
					reason: res?.message || "Could not add prompt",
				});
			}
		}

		setSubmitBusy(false);
		setProgress(null);

		if (created.length === 0) {
			setFailures(failed);
			setSubmitError(`None saved. Failed (${failed.length}).`);
			return;
		}

		const successMessage =
			created.length === 1
				? "Prompt added"
				: failed.length === 0
					? `Added ${created.length} prompts`
					: `Added ${created.length} of ${lines.length}  -  ${failed.length} failed`;

		onCompleted?.(successMessage);

		if (failed.length === 0) {
			onClose();
			return;
		}

		setPromptText(failed.map((f) => f.text).join("\n"));
		setInfo(`Saved ${created.length} of ${lines.length}.`);
		setFailures(failed);
		setSubmitError(`Failed (${failed.length})  -  remaining lines left in the box.`);
	}

	const primaryLabel = busy
		? progress && progress.n > 1
			? `Adding ${progress.k}/${progress.n}…`
			: "Adding…"
		: validLines.length > 1
			? `Add ${validLines.length} prompts`
			: "Add prompt";

	const addPromptModalWidth = "min(60rem, calc(100vw - 3rem))";

	return (
		<Modal
			isOpen={isOpen}
			toggle={busy ? undefined : onClose}
			centered
			size="xl"
			modalClassName="monitoring__add-prompt-modal-overlay"
			className="monitoring__add-prompt-modal monitoring__add-prompt-modal--wide"
			style={{
				"--bs-modal-width": addPromptModalWidth,
				width: addPromptModalWidth,
				maxWidth: addPromptModalWidth,
			}}
			contentClassName="monitoring__add-prompt-modal-content"
			backdrop={busy ? "static" : true}
			keyboard={!busy}
		>
			<ModalHeader
				tag="div"
				toggle={busy ? undefined : onClose}
				className="monitoring__add-prompt-modal-header"
			>
				<div className="monitoring__add-prompt-modal-head">
					<div className="monitoring__add-prompt-modal-title">Add prompt</div>
					<p className="monitoring__add-prompt-modal-subtitle mb-0">
						Classified on save. Every field below is optional  -  Auto-detect fills
						whatever you leave alone, and you can override anything.
					</p>
				</div>
			</ModalHeader>
			<ModalBody>
				<div className="monitoring__add-prompt-columns monitoring__add-prompt-columns--two">
					<div className="monitoring__add-prompt-column">
						<div className="monitoring__add-prompt-field">
							<div className="monitoring__add-prompt-label-row">
								<Label className="monitoring__field-label mb-0">Prompt</Label>
								<span
									className={`monitoring__add-prompt-line-count${
										overLimit ? " monitoring__add-prompt-line-count--over" : ""
									}`}
									aria-live="polite"
								>
									{startedCount}/{PROMPT_BULK_MAX_LINES}
								</span>
							</div>
							<Input
								type="textarea"
								innerRef={textareaRef}
								rows={4}
								className="monitoring__add-prompt-textarea"
								placeholder={`One prompt per line (max ${PROMPT_BULK_MAX_LINES}), e.g.\n${placeholderExample}\nanother prompt on the next line`}
								value={promptText}
								disabled={busy}
								onChange={(e) => {
									setPromptText(e.target.value);
									setInfo(null);
								}}
							/>
							<div className="monitoring__add-prompt-csv-row monitoring__add-prompt-csv-row--inline">
								<div className="monitoring__add-prompt-csv-help text-muted small">
									<span className="d-block">
										One prompt per line  -  max {PROMPT_BULK_MAX_LINES}.
									</span>
									<span className="d-block">Or upload a CSV.</span>
								</div>
								<div className="monitoring__add-prompt-csv-actions">
									<a
										href={`${process.env.PUBLIC_URL || ""}/sample-bulk-prompts.csv`}
										download="sample-bulk-prompts.csv"
										className="monitoring__add-prompt-csv-link"
									>
										Sample CSV
									</a>
									<Button
										type="button"
										color="primary"
										size="sm"
										disabled={busy}
										onClick={() => csvFileRef.current?.click()}
									>
										Upload CSV
									</Button>
									<input
										ref={csvFileRef}
										type="file"
										accept=".csv,text/csv"
										className="d-none"
										onChange={(e) => onCsvFile(e.target.files?.[0])}
									/>
								</div>
							</div>
						</div>
						<div className="monitoring__add-prompt-field">
							<Label className="monitoring__field-label">Topic</Label>
							<MonitoringSelect
								aria-label="Topic"
								options={topicOptions}
								value={topicChoice}
								disabled={busy}
								dropUp
								onChange={setTopicChoice}
							/>
							{topicChoice === TOPIC_NEW && (
								<Input
									className="mt-1"
									placeholder="New topic name"
									value={newTopicName}
									disabled={busy}
									onChange={(e) => setNewTopicName(e.target.value)}
								/>
							)}
							<p className="monitoring__field-help text-muted mb-0">
								Auto-detect clusters this prompt into your existing topics, and only
								creates a new one when nothing fits. Below the confidence threshold it
								stays Unassigned rather than being filed into the wrong topic.
							</p>
						</div>
						<div className="monitoring__add-prompt-grid monitoring__add-prompt-grid--intent">
							<div className="monitoring__add-prompt-field">
								<Label className="monitoring__field-label">Search intent</Label>
								<MonitoringSelect
									aria-label="Search intent"
									options={INTENT_OPTIONS}
									value={intent}
									disabled={busy}
									dropUp
									onChange={setIntent}
								/>
							</div>
							<div className="monitoring__add-prompt-field">
								<Label className="monitoring__field-label">Journey stage</Label>
								<MonitoringSelect
									aria-label="Journey stage"
									options={JOURNEY_OPTIONS}
									value={journeyStage}
									disabled={busy}
									dropUp
									onChange={setJourneyStage}
								/>
							</div>
						</div>
					</div>

					<div className="monitoring__add-prompt-column">
						<div className="monitoring__brand-mention-box">
							<p className="mb-0">
								<strong>Brand mention</strong>  -  detected from your brand name. If a
								prompt trivially names you, it is excluded from Brand Share of Voice.
							</p>
							<label className="monitoring__check-row">
								<input
									type="checkbox"
									checked={forceBranded}
									disabled={busy}
									onChange={(e) => setForceBranded(e.target.checked)}
								/>
								<span>This prompt names my brand</span>
							</label>
							<p className="monitoring__field-help text-muted mb-0">
								Leave unchecked to auto-detect; check to force it.
							</p>
						</div>
						<div className="monitoring__add-prompt-field mb-0">
							<Label className="monitoring__field-label">Run as (optional)</Label>
							<p className="monitoring__field-help text-muted">
								Each choice is tracked as its own prompt from the next run.
							</p>
							<label className="monitoring__check-row">
								<input
									type="checkbox"
									checked={noPersona}
									disabled={busy || (noPersona && personaIds.length === 0)}
									onChange={(e) => setNoPersona(e.target.checked)}
								/>
								<span>No persona</span>
							</label>
							<div className="monitoring__add-prompt-persona-scroll">
								{personasLoading ? (
									<div
										className="monitoring__add-prompt-persona-loading"
										role="status"
										aria-live="polite"
									>
										<Spinner size="sm" />
										<span className="text-muted small">Loading personas…</span>
									</div>
								) : personas.length === 0 ? (
									<p className="text-muted small mb-0">
										No personas yet  -  add them under AI Writing Assistant → Settings.
									</p>
								) : (
									<ul className="list-unstyled mb-0">
										{personas.map((persona) => {
											const id = Number(persona.id);
											const checked = personaIds.includes(id);
											const atCap =
												maxPersonas > 0 && !checked && personaIds.length >= maxPersonas;
											return (
												<li key={id} className="mb-1">
													<label className="monitoring__check-row mb-0">
														<input
															type="checkbox"
															checked={checked}
															disabled={busy || atCap}
															onChange={() => toggleAddPersona(id)}
														/>
														<span>
															{persona.name}
															{persona.profession && persona.profession !== persona.name
																? ` · ${persona.profession}`
																: ""}
														</span>
													</label>
												</li>
											);
										})}
									</ul>
								)}
							</div>
						</div>
					</div>
				</div>

				{info && (
					<p className="monitoring__add-prompt-info text-muted small mb-0" role="status">
						{info}
					</p>
				)}
				{submitError && (
					<div className="monitoring__form-error mb-0" role="alert">
						<p className="mb-0">{submitError}</p>
						{failures.length > 0 && (
							<ul className="monitoring__add-prompt-failures mb-0 mt-2">
								{failures.map((f) => (
									<li key={f.text}>
										<strong>{f.text}</strong>
										<span className="text-muted">  -  {f.reason}</span>
									</li>
								))}
							</ul>
						)}
					</div>
				)}
			</ModalBody>
			<ModalFooter className="monitoring__add-prompt-modal-footer">
				<Button color="secondary" outline onClick={onClose} disabled={busy}>
					Cancel
				</Button>
				<Button
					color="primary"
					onClick={handleSubmit}
					disabled={busy || overLimit || validLines.length === 0}
				>
					{busy ? (
						<>
							<Spinner size="sm" className="me-2" />
							{primaryLabel}
						</>
					) : (
						primaryLabel
					)}
				</Button>
			</ModalFooter>
		</Modal>
	);
}

function EditClassificationModal({
	isOpen,
	onClose,
	onSubmit,
	busy,
	topics = [],
	row,
	error,
}) {
	const [topicId, setTopicId] = useState("");
	const [intent, setIntent] = useState("");
	const [journeyStage, setJourneyStage] = useState("");
	const [isBranded, setIsBranded] = useState(false);

	useEffect(() => {
		if (!isOpen || !row) return;
		setTopicId(row.topicId != null ? String(row.topicId) : "");
		setIntent(row.intent || "");
		setJourneyStage(row.journeyStage || "");
		setIsBranded(Boolean(row.isBranded));
	}, [isOpen, row]);

	const topicOptions = useMemo(
		() => [
			{ value: "", label: "Unassigned" },
			...topics.map((t) => ({
				value: String(t.topicId),
				label: t.name,
			})),
		],
		[topics]
	);

	async function handleSubmit() {
		if (busy || !row) return;
		await onSubmit?.({
			topicId: topicId ? Number(topicId) : null,
			intent: intent || null,
			journeyStage: journeyStage || null,
			isBranded,
			topicName:
				topicOptions.find((o) => o.value === topicId)?.label || "Unassigned",
		});
	}

	return (
		<Modal
			isOpen={isOpen}
			toggle={busy ? undefined : onClose}
			centered
			modalClassName="monitoring__add-prompt-modal-overlay"
			className="monitoring__add-prompt-modal monitoring__edit-classification-modal"
			backdrop={busy ? "static" : true}
			keyboard={!busy}
		>
			<ModalHeader toggle={busy ? undefined : onClose}>
				Edit classification
			</ModalHeader>
			<ModalBody>
				<div className="monitoring__add-prompt-field">
					<Label className="monitoring__field-label">Prompt</Label>
					<div className="monitoring__edit-prompt-readonly" aria-readonly="true">
						{row?.prompt || ""}
					</div>
				</div>

				<div className="monitoring__add-prompt-field">
					<Label className="monitoring__field-label">Topic</Label>
					<MonitoringSelect
						aria-label="Topic"
						options={topicOptions}
						value={topicId}
						disabled={busy}
						onChange={setTopicId}
					/>
				</div>

				<div className="monitoring__add-prompt-grid">
					<div className="monitoring__add-prompt-field">
						<Label className="monitoring__field-label">Search intent</Label>
						<MonitoringSelect
							aria-label="Search intent"
							options={EDIT_INTENT_OPTIONS}
							value={intent}
							disabled={busy}
							onChange={setIntent}
						/>
					</div>
					<div className="monitoring__add-prompt-field">
						<Label className="monitoring__field-label">Journey stage</Label>
						<MonitoringSelect
							aria-label="Journey stage"
							options={EDIT_JOURNEY_OPTIONS}
							value={journeyStage}
							disabled={busy}
							onChange={setJourneyStage}
						/>
					</div>
				</div>

				<div className="monitoring__brand-mention-box">
					<p className="mb-0">
						<strong>Brand mention</strong> - detected from your brand name. If a
						prompt trivially names you, it is excluded from Brand Share of Voice.
					</p>
					<label className="monitoring__check-row">
						<input
							type="checkbox"
							checked={isBranded}
							disabled={busy}
							onChange={(e) => setIsBranded(e.target.checked)}
						/>
						<span>This prompt names my brand</span>
					</label>
					<p className="monitoring__field-help text-muted mb-0">
						Leave unchecked to auto-detect; check to force it.
					</p>
				</div>

				{error && (
					<p className="monitoring__form-error mb-0" role="alert">
						{error}
					</p>
				)}
			</ModalBody>
			<ModalFooter>
				<Button color="secondary" outline onClick={onClose} disabled={busy}>
					Cancel
				</Button>
				<Button color="primary" onClick={handleSubmit} disabled={busy || !row}>
					{busy ? (
						<>
							<Spinner size="sm" className="me-2" />
							Saving…
						</>
					) : (
						"Save changes"
					)}
				</Button>
			</ModalFooter>
		</Modal>
	);
}

function VariantRunIcon({ personaId }) {
	const Icon = personaId === 0 ? User : UserRound;
	const label = personaId === 0 ? "No persona run" : "Persona run";
	return (
		<span className="monitoring__prompt-variant-icon" aria-hidden title={label}>
			<Icon size={15} strokeWidth={2} />
		</span>
	);
}

function PromptRow({
	row,
	selected,
	expanded,
	onToggleSelect,
	onToggleExpand,
	onRetire,
	onReactivate,
	onEdit,
	onAnalysis,
	onPersonas,
	analysing,
	websiteId,
	onViewRaw,
	variantLabel,
	variantPersonaId,
}) {
	const retired = String(row.status || "").toLowerCase() === "retired";
	const isVariant = Boolean(variantLabel);
	const personaIdForIcon =
		variantPersonaId != null ? variantPersonaId : row.variantPersonaId ?? 0;
	const canViewAnalysis = Boolean(
		row.latestAnalysis?.viewAnalysis && row.latestAnalysis?.analysisId != null
	);
	const analysisLabel = analysing
		? "Analysing…"
		: canViewAnalysis
			? "View analysis"
			: "Analyse";
	const analysisTitle = canViewAnalysis
		? "Open today's saved analysis"
		: "Generate a new fan-out and content-strategy analysis across ChatGPT, Gemini and Claude";
	return (
		<>
			<tr
				className={`monitoring__prompt-tr${
					isVariant ? " monitoring__prompt-tr--variant" : ""
				}${selected ? " monitoring__prompt-tr--selected" : ""}${
					expanded ? " monitoring__tr--expanded" : ""
				}${retired ? " monitoring__tr--retired" : ""}`}
			>
				<td className="monitoring__td-check">
					{isVariant ? (
						<span className="monitoring__td-check-spacer" aria-hidden />
					) : (
						<input
							type="checkbox"
							checked={selected}
							onChange={() => onToggleSelect(row.promptId)}
							aria-label={`Select ${row.prompt}`}
						/>
					)}
				</td>
				<td className="monitoring__td-prompt">
					{isVariant ? (
						<div className="monitoring__prompt-variant-label">
							<VariantRunIcon personaId={personaIdForIcon} />
							<span>{variantLabel}</span>
						</div>
					) : (
						<button
							type="button"
							className="monitoring__prompt-text-btn"
							onClick={() => onToggleExpand(row.id)}
						>
							{row.prompt}
						</button>
					)}
					{!isVariant && (
						<div className="monitoring__prompt-inline-chips">
							{retired && (
								<span className="monitoring__retired-chip">Retired</span>
							)}
							<IntentChip intent={row.intent} />
							<JourneyChip stage={row.journeyStage} />
							{row.isBranded && (
								<span className="monitoring__branded-chip">Branded</span>
							)}
						</div>
					)}
				</td>
				<td>
					{isVariant ? null : (
						<SourcePill sourceKey={row.source} label={row.sourceLabel} />
					)}
				</td>
				<td>
					<div className="monitoring__engine-chips">
						{row.enginesCited.length ? (
							row.enginesCitedRaw.map((eng, index) => (
								<ProviderChip
									key={eng}
									provider={eng}
									label={row.enginesCited[index]}
									size={12}
									className="monitoring__engine-chip"
								/>
							))
						) : (
							<span className="text-muted">None yet</span>
						)}
					</div>
				</td>
				<td className="monitoring__td-num">{row.cited}</td>
				<td className="monitoring__td-num">
					{row.avgPosition != null ? Number(row.avgPosition).toFixed(1) : " - "}
				</td>
				<td>
					<span
						className={`monitoring__sentiment-badge monitoring__sentiment-badge--${row.sentiment}`}
					>
						{row.sentiment === "positive"
							? "Positive"
							: row.sentiment === "negative"
								? "Negative"
								: "Neutral"}
					</span>
				</td>
				<td>
					<MonitoringSparkline
						values={row.trend}
						color={row.trendDeclining ? "#EA580C" : "#4F46E5"}
						width={72}
						height={28}
					/>
				</td>
				<td className="monitoring__td-actions">
					<div className="monitoring__td-actions-inner">
						<button
							type="button"
							className="monitoring__trend-toggle"
							aria-expanded={expanded}
							onClick={() => onToggleExpand(row.id)}
						>
							{expanded ? "Hide trend" : "Trend"}
						</button>
						<button
							type="button"
							className="monitoring__trend-toggle monitoring__trend-toggle--analysis"
							onClick={() => onAnalysis(row)}
							disabled={analysing}
							title={analysisTitle}
							aria-label={
								canViewAnalysis
									? `View saved analysis for ${row.prompt}`
									: `Analyse ${row.prompt}`
							}
						>
							<Microscope size={13} aria-hidden />
							{analysisLabel}
						</button>
						{onPersonas && !isVariant && !retired && (
							<button
								type="button"
								className="monitoring__trend-toggle"
								onClick={() => onPersonas(row)}
							>
								Attach personas
							</button>
						)}
						{!isVariant && (
							<button
								type="button"
								className="monitoring__trend-toggle monitoring__trend-toggle--edit"
								onClick={() => onEdit(row)}
								aria-label={`Edit classification for ${row.prompt}`}
							>
								<Settings size={12} aria-hidden />
								Edit
							</button>
						)}
						{retired ? (
							<button
								type="button"
								className="monitoring__trend-toggle monitoring__trend-toggle--warning"
								onClick={() => onReactivate(row)}
							>
								Re-enable
							</button>
						) : (
							<button
								type="button"
								className="monitoring__trend-toggle monitoring__trend-toggle--danger"
								onClick={() => onRetire(row)}
							>
								Retire
							</button>
						)}
					</div>
				</td>
			</tr>
			{expanded && (
				<tr className="monitoring__tr-detail">
					<td colSpan={9}>
						<TrendDrilldown
							websiteId={websiteId}
							promptId={row.promptId}
							declining={row.trendDeclining}
							onViewRaw={() => onViewRaw(row)}
						/>
					</td>
				</tr>
			)}
		</>
	);
}

function PromptGroupBlock({
	row,
	personaLabels,
	selected,
	onToggleSelect,
	expandedId,
	onToggleExpand,
	onRetire,
	onReactivate,
	onEdit,
	onAnalysis,
	onPersonas,
	analysingPromptId,
	websiteId,
	onViewRaw,
}) {
	const [collapsed, setCollapsed] = useState(false);
	const retired = String(row.status || "").toLowerCase() === "retired";
	const labelOf = (v) =>
		v.personaId === 0
			? "No persona"
			: personaLabels.get(v.personaId) || v.personaName || `Persona #${v.personaId}`;

	return (
		<>
			<tr
				className={`monitoring__prompt-tr monitoring__prompt-tr--group${
					selected ? " monitoring__prompt-tr--selected" : ""
				}${retired ? " monitoring__tr--retired" : ""}`}
			>
				<td className="monitoring__td-check">
					<input
						type="checkbox"
						checked={selected}
						onChange={() => onToggleSelect(row.promptId)}
						aria-label={`Select ${row.prompt}`}
					/>
				</td>
				<td className="monitoring__td-prompt">
					<div className="monitoring__prompt-group-head">
						<span className="monitoring__prompt-text-btn monitoring__prompt-text-btn--static">
							{row.prompt}
						</span>
					</div>
					<div className="monitoring__prompt-inline-chips">
						<IntentChip intent={row.intent} />
						<JourneyChip stage={row.journeyStage} />
						{row.isBranded && (
							<span className="monitoring__branded-chip">Branded</span>
						)}
					</div>
				</td>
				<td colSpan={7} className="monitoring__prompt-group-rollup">
					<div className="monitoring__prompt-group-rollup-inner">
						<div className="monitoring__prompt-group-rollup-main">
							<SourcePill sourceKey={row.source} label={row.sourceLabel} />
							<span className="monitoring__prompt-group-rollup-text text-muted small">
								{row.variants.length} run{row.variants.length === 1 ? "" : "s"}  - 
								retire or re-enable each run on its row below
							</span>
						</div>
						<div className="monitoring__td-actions-inner monitoring__prompt-group-rollup-actions">
							<button
								type="button"
								className={`monitoring__trend-toggle monitoring__trend-toggle--personas-list${
									!collapsed ? " is-expanded" : ""
								}`}
								aria-expanded={!collapsed}
								onClick={() => setCollapsed((c) => !c)}
							>
								<ChevronDown
									size={12}
									className={`monitoring__topic-chevron${
										!collapsed ? " is-open" : ""
									}`}
									aria-hidden
								/>
								{collapsed ? "Show personas" : "Hide personas"}
							</button>
							{!retired && (
								<button
									type="button"
									className="monitoring__trend-toggle"
									onClick={() => onPersonas(row)}
								>
									Attach personas
								</button>
							)}
							{!retired && (
								<button
									type="button"
									className="monitoring__trend-toggle monitoring__trend-toggle--edit"
									onClick={() => onEdit(row)}
								>
									<Settings size={12} aria-hidden />
									Edit
								</button>
							)}
						</div>
					</div>
				</td>
			</tr>
			{!collapsed &&
				row.variants.map((variant) => {
					const vRow = variantRowFromParent(row, variant, labelOf(variant));
					const off =
						variant.state === "retired" &&
						String(row.status || "").toLowerCase() !== "retired";
					if (off) {
						return (
							<tr
								key={vRow.id}
								className="monitoring__prompt-tr monitoring__prompt-tr--variant monitoring__tr--retired"
							>
								<td className="monitoring__td-check">
									<span className="monitoring__td-check-spacer" aria-hidden />
								</td>
								<td className="monitoring__td-prompt">
									<div className="monitoring__prompt-variant-label">
										<VariantRunIcon personaId={variant.personaId} />
										<span>
											{labelOf(variant)}{" "}
											<span className="monitoring__retired-chip">Off</span>
										</span>
									</div>
								</td>
								<td colSpan={7} className="monitoring__prompt-group-rollup">
									<div className="monitoring__prompt-group-rollup-inner">
										<span className="monitoring__prompt-group-rollup-text text-muted small">
											Not running
										</span>
										<div className="monitoring__td-actions-inner monitoring__prompt-group-rollup-actions">
											<button
												type="button"
												className="monitoring__trend-toggle monitoring__trend-toggle--warning"
												onClick={() =>
													onReactivate({
														promptId: variant.promptId,
														parentPromptId: row.promptId,
														variantPersonaId: variant.personaId,
													})
												}
											>
												Re-enable
											</button>
										</div>
									</div>
								</td>
							</tr>
						);
					}
					return (
						<PromptRow
							key={vRow.id}
							row={vRow}
							variantLabel={vRow.variantLabel}
							variantPersonaId={variant.personaId}
							selected={false}
							expanded={expandedId === vRow.id}
							onToggleSelect={onToggleSelect}
							onToggleExpand={onToggleExpand}
							onRetire={onRetire}
							onReactivate={onReactivate}
							onEdit={onEdit}
							onAnalysis={onAnalysis}
							analysing={analysingPromptId === vRow.promptId}
							websiteId={websiteId}
							onViewRaw={onViewRaw}
						/>
					);
				})}
		</>
	);
}

function PromptsTable({
	prompts,
	personaLabels,
	selectedIds,
	expandedId,
	onToggleSelect,
	onToggleExpand,
	onRetire,
	onReactivate,
	onEdit,
	onAnalysis,
	onPersonas,
	analysingPromptId,
	websiteId,
	onViewRaw,
	onToggleAll,
}) {
	const allIds = prompts.map((p) => p.promptId);
	const allSelected =
		allIds.length > 0 && allIds.every((id) => selectedIds.has(id));
	const someSelected = allIds.some((id) => selectedIds.has(id));

	return (
		<table className="monitoring__table monitoring__prompts-table">
			<thead>
				<tr>
					<th scope="col" className="monitoring__th-check">
						<input
							type="checkbox"
							checked={allSelected}
							ref={(el) => {
								if (el) el.indeterminate = someSelected && !allSelected;
							}}
							onChange={() => onToggleAll(allIds, !allSelected)}
							aria-label="Select all prompts in group"
						/>
					</th>
					<th scope="col">Prompt</th>
					<th scope="col">Source</th>
					<th scope="col">Engines cited</th>
					<th scope="col" className="monitoring__th-num">
						Cited
					</th>
					<th scope="col" className="monitoring__th-num">
						Avg position
					</th>
					<th scope="col">Sentiment</th>
					<th scope="col">Trend</th>
					<th scope="col">
						<span className="monitoring__sr-only">Actions</span>
					</th>
				</tr>
			</thead>
			<tbody>
				{prompts.map((row) =>
					isPromptGrouped(row) ? (
						<PromptGroupBlock
							key={row.id}
							row={row}
							personaLabels={personaLabels}
							selected={selectedIds.has(row.promptId)}
							expandedId={expandedId}
							onToggleSelect={onToggleSelect}
							onToggleExpand={onToggleExpand}
							onRetire={onRetire}
							onReactivate={onReactivate}
							onEdit={onEdit}
							onAnalysis={onAnalysis}
							onPersonas={onPersonas}
							analysingPromptId={analysingPromptId}
							websiteId={websiteId}
							onViewRaw={onViewRaw}
						/>
					) : (
						<PromptRow
							key={row.id}
							row={row}
							selected={selectedIds.has(row.promptId)}
							expanded={expandedId === row.id}
							onToggleSelect={onToggleSelect}
							onToggleExpand={onToggleExpand}
							onRetire={onRetire}
							onReactivate={onReactivate}
							onEdit={onEdit}
							onAnalysis={onAnalysis}
							onPersonas={onPersonas}
							analysing={analysingPromptId === row.promptId}
							websiteId={websiteId}
							onViewRaw={onViewRaw}
						/>
					)
				)}
			</tbody>
		</table>
	);
}

export default function MonitoringPrompts({
	websiteId,
	refreshSeq = 0,
	onNavLockChange,
}) {
	const navigate = useNavigate();
	const [statusFilter, setStatusFilter] = useState("active");
	const [analysingPromptId, setAnalysingPromptId] = useState(null);
	const [engine, setEngine] = useState("");
	const [source, setSource] = useState("all");
	const [intent, setIntent] = useState("all");
	const [journey, setJourney] = useState("all");
	const [searchInput, setSearchInput] = useState("");
	const [search, setSearch] = useState("");
	const [addOpen, setAddOpen] = useState(false);
	const [addSubmitBusy, setAddSubmitBusy] = useState(false);
	const [editRow, setEditRow] = useState(null);
	const [editError, setEditError] = useState(null);
	const [editBusy, setEditBusy] = useState(false);
	const [actionBanner, setActionBanner] = useState(null);
	const [expandedId, setExpandedId] = useState(null);
	const [rawFor, setRawFor] = useState(null);
	const [list, setList] = useState(null);
	const [topicsPayload, setTopicsPayload] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [selectedIds, setSelectedIds] = useState(() => new Set());
	const [bulkTopicId, setBulkTopicId] = useState(null);
	const [bulkBusy, setBulkBusy] = useState(false);
	const [renameTopicId, setRenameTopicId] = useState(null);
	const [renameValue, setRenameValue] = useState("");
	const [mergeTopicId, setMergeTopicId] = useState(null);
	const [mergeTargetId, setMergeTargetId] = useState(null);
	const [actionBusy, setActionBusy] = useState(null);
	const [autoAssignBusy, setAutoAssignBusy] = useState(false);
	const [openTopicIds, setOpenTopicIds] = useState(() => new Set());
	const topicAccordionInitRef = useRef(false);
	const [personaLabels, setPersonaLabels] = useState(() => new Map());
	const [personasModalRow, setPersonasModalRow] = useState(null);

	useEffect(() => {
		const t = window.setTimeout(() => setSearch(searchInput), 200);
		return () => window.clearTimeout(t);
	}, [searchInput]);

	const load = useCallback(async () => {
		if (!websiteId) {
			setLoading(false);
			setError("Missing website");
			return;
		}
		setLoading(true);
		setError(null);
		const [promptsRes, topicsRes] = await Promise.all([
			getMonitoringPrompts(websiteId, {
				engine: engine || undefined,
				status:
					statusFilter === "all" || statusFilter === "retired"
						? statusFilter
						: undefined,
			}),
			getMonitoringTopics(websiteId),
		]);

		if (!promptsRes.ok) {
			setError(promptsRes.message || "Failed to load prompts");
			setList(null);
			setLoading(false);
			return;
		}

		setList(
			adaptPromptsList({
				prompts: promptsRes.prompts,
				total: promptsRes.total,
				maxPersonasPerPrompt: promptsRes.maxPersonasPerPrompt,
				webSearch: promptsRes.webSearch,
				engineFilter: promptsRes.engineFilter,
			})
		);

		if (topicsRes.ok) {
			setTopicsPayload(
				adaptTopicsPayload({
					topics: topicsRes.topics,
					unassigned: topicsRes.unassigned,
				})
			);
		} else {
			setTopicsPayload(adaptTopicsPayload({ topics: [], unassigned: null }));
			toast.warn(topicsRes.message || "Could not load topics");
		}
		setLoading(false);
	}, [websiteId, engine, statusFilter]);

	useEffect(() => {
		load();
	}, [load, refreshSeq]);

	useEffect(() => {
		setActionBanner(null);
	}, [statusFilter]);

	const filters = list?.filters || {};
	const maxPersonasPerPrompt = list?.maxPersonasPerPrompt ?? 0;
	const rows = useMemo(() => list?.rows || [], [list?.rows]);

	useEffect(() => {
		if (!websiteId) return undefined;
		const needsPersonas =
			addOpen || rows.some((r) => r.variants?.some((v) => v.personaId !== 0));
		if (!needsPersonas) return undefined;
		let cancelled = false;
		listMonitoringWritingAssistantPersonas(websiteId).then((res) => {
			if (cancelled || !res.ok) return;
			const map = new Map();
			(res.personas || []).forEach((p) => {
				map.set(Number(p.id), p.name || `Persona #${p.id}`);
			});
			setPersonaLabels(map);
		});
		return () => {
			cancelled = true;
		};
	}, [websiteId, rows, addOpen]);

	useEffect(() => {
		const locked =
			addOpen ||
			addSubmitBusy ||
			editRow != null ||
			editBusy ||
			personasModalRow != null ||
			rawFor != null;
		onNavLockChange?.(locked);
		return () => onNavLockChange?.(false);
	}, [
		addOpen,
		addSubmitBusy,
		editRow,
		editBusy,
		personasModalRow,
		rawFor,
		onNavLockChange,
	]);

	const filteredRows = useMemo(() => {
		const q = search.trim().toLowerCase();
		return rows.filter((row) => {
			if (source !== "all") {
				const raw = String(row.sourceRaw || "").toLowerCase();
				const key = String(row.source || "").toLowerCase();
				if (source === "generated") {
					if (key !== "generated" && !raw.includes("generat") && !raw.includes("keyword")) {
						return false;
					}
					if (raw.includes("suggest") || key === "ai-suggested") return false;
				} else if (source === "ai_suggested") {
					if (
						key !== "ai-suggested" &&
						!raw.includes("suggest") &&
						!raw.includes("ai_suggested")
					) {
						return false;
					}
				} else if (source === "baseline_audit") {
					if (
						key !== "baseline-audit" &&
						!(raw.includes("baseline") || raw.includes("audit"))
					) {
						return false;
					}
					if (raw.includes("manual")) return false;
				} else if (source === "manual") {
					if (key !== "manual" && !raw.includes("manual")) return false;
				} else if (raw !== source && key !== source.replace(/_/g, "-")) {
					return false;
				}
			}
			if (intent !== "all" && row.intent !== intent) return false;
			if (journey !== "all" && row.journeyStage !== journey) return false;
			if (q && !row.prompt.toLowerCase().includes(q)) return false;
			return true;
		});
	}, [rows, source, intent, journey, search]);

	const grouped = useMemo(
		() => buildPromptTopicGroups(filteredRows, topicsPayload),
		[filteredRows, topicsPayload]
	);

	const allTopics = topicsPayload?.topics || [];

	const examplePrompt = useMemo(() => {
		const candidates = rows.filter((r) => r.prompt?.trim());
		return (
			candidates.find((r) => !r.isBranded && r.prompt.length <= 60)?.prompt ||
			candidates.find((r) => r.prompt.length <= 60)?.prompt ||
			candidates[0]?.prompt ||
			""
		);
	}, [rows]);

	useEffect(() => {
		if (topicAccordionInitRef.current) return;
		const hasTopics = grouped.groups.length > 0;
		const hasUnassigned = grouped.unassigned.prompts.length > 0;
		if (!hasTopics && !hasUnassigned) return;
		const next = new Set();
		if (hasTopics) next.add(String(grouped.groups[0].topicId));
		else if (hasUnassigned) next.add("unassigned");
		setOpenTopicIds(next);
		topicAccordionInitRef.current = true;
	}, [grouped]);

	function toggleTopicOpen(topicKey) {
		const key = String(topicKey);
		setOpenTopicIds((prev) => {
			const next = new Set(prev);
			if (next.has(key)) next.delete(key);
			else next.add(key);
			return next;
		});
	}

	function toggleSelect(promptId) {
		setSelectedIds((prev) => {
			const next = new Set(prev);
			if (next.has(promptId)) next.delete(promptId);
			else next.add(promptId);
			return next;
		});
	}

	function toggleAllInGroup(ids, select) {
		setSelectedIds((prev) => {
			const next = new Set(prev);
			ids.forEach((id) => {
				if (select) next.add(id);
				else next.delete(id);
			});
			return next;
		});
	}

	function clearSelection() {
		setSelectedIds(new Set());
		setBulkTopicId(null);
	}

	async function submitAddPrompt(payload) {
		return addMonitoringPrompt(websiteId, payload);
	}

	function handlePromptsAdded(message) {
		toast.success(message);
		load();
	}

	async function handleRetire(row) {
		const ctx = getPromptRunLifecycleContext(row);
		let res;
		if (shouldToggleNoPersonaRun(ctx)) {
			res = await setMonitoringPromptNoPersona(
				websiteId,
				ctx.parentPromptId,
				false
			);
			if (!res.ok) {
				toast.error(res.message || "Could not turn off no-persona run");
				return;
			}
			setActionBanner(
				"No-persona run turned off  -  other persona runs are unchanged."
			);
		} else {
			res = await retireMonitoringPrompt(websiteId, ctx.variantPromptId);
			if (!res.ok) {
				toast.error(res.message || "Could not retire prompt");
				return;
			}
			setActionBanner(
				"Prompt retired  -  it stops running but stays in history."
			);
		}
		load();
	}

	async function handleReactivate(row) {
		const ctx = getPromptRunLifecycleContext(row);
		let res;
		if (shouldToggleNoPersonaRun(ctx)) {
			res = await setMonitoringPromptNoPersona(
				websiteId,
				ctx.parentPromptId,
				true
			);
			if (!res.ok) {
				toast.error(res.message || "Could not re-enable no-persona run");
				return;
			}
			setActionBanner("No-persona run re-enabled for the next schedule.");
		} else {
			res = await reactivateMonitoringPrompt(websiteId, ctx.variantPromptId);
			if (!res.ok) {
				toast.error(res.message || "Could not re-enable prompt");
				return;
			}
			setActionBanner("Prompt re-enabled  -  it will run on the next schedule.");
		}
		load();
	}

	function handleAnalysis(row) {
		if (analysingPromptId != null) return;

		const cached = row.latestAnalysis;
		if (cached?.viewAnalysis && cached.analysisId != null) {
			navigate(
				`/aivisibility/monitoring/analysis?analysisId=${cached.analysisId}`
			);
			return;
		}

		setAnalysingPromptId(row.promptId);
		analyseMonitoringPrompt(websiteId, row.promptId)
			.then((res) => {
				if (!res.ok) {
					toast.error(res.message || "Could not start analysis");
					return;
				}
				const analysisId = res.analysis?.analysis_id;
				if (analysisId == null) {
					toast.error("Analysis started but no id returned");
					return;
				}
				load();
				navigate(
					`/aivisibility/monitoring/analysis?analysisId=${analysisId}`
				);
			})
			.finally(() => setAnalysingPromptId(null));
	}

	async function handleEditSave(fields) {
		if (!editRow) return;
		setEditBusy(true);
		setEditError(null);
		const payload = {
			isBranded: Boolean(fields.isBranded),
		};
		if (fields.topicId != null && !Number.isNaN(Number(fields.topicId))) {
			payload.topicId = Number(fields.topicId);
		}
		if (fields.intent) payload.intent = fields.intent;
		if (fields.journeyStage) payload.journeyStage = fields.journeyStage;
		const res = await updateMonitoringPrompt(
			websiteId,
			editRow.promptId,
			payload
		);
		setEditBusy(false);
		if (!res.ok) {
			setEditError(res.message || "Could not update prompt");
			toast.error(res.message || "Could not update prompt");
			return;
		}
		const topicName = fields.topicName || editRow.topic || "Unassigned";
		const intentPart =
			intentLabel(fields.intent || editRow.intent) || "unclassified";
		const journeyPart =
			journeyLabel(fields.journeyStage || editRow.journeyStage) ||
			"unclassified";
		const brandedPart = fields.isBranded ? "branded" : "unbranded";
		setActionBanner(
			`Updated  -  topic "${topicName}", ${intentPart}, ${journeyPart}, ${brandedPart}.`
		);
		setEditRow(null);
		load();
	}

	async function handleBulkMove() {
		if (bulkTopicId == null || selectedIds.size === 0 || bulkBusy) return;
		setBulkBusy(true);
		const res = await moveMonitoringPrompts(
			websiteId,
			Array.from(selectedIds),
			bulkTopicId
		);
		setBulkBusy(false);
		if (!res.ok) {
			toast.error(res.message || "Could not move prompts");
			return;
		}
		toast.success(`Moved ${selectedIds.size} prompt(s)`);
		clearSelection();
		load();
	}

	async function handleRenameApply(topicId) {
		const name = renameValue.trim();
		if (!name) {
			toast.error("Topic name is required");
			return;
		}
		setActionBusy(`rename-${topicId}`);
		const res = await renameMonitoringTopic(websiteId, topicId, name);
		setActionBusy(null);
		if (!res.ok) {
			toast.error(res.message || "Could not rename topic");
			return;
		}
		toast.success("Topic renamed");
		setRenameTopicId(null);
		load();
	}

	async function handleMergeApply(sourceTopicId) {
		if (mergeTargetId == null) {
			toast.error("Pick a target topic");
			return;
		}
		setActionBusy(`merge-${sourceTopicId}`);
		const res = await mergeMonitoringTopics(
			websiteId,
			sourceTopicId,
			mergeTargetId
		);
		setActionBusy(null);
		if (!res.ok) {
			toast.error(res.message || "Could not merge topics");
			return;
		}
		toast.success("Topics merged");
		setMergeTopicId(null);
		setMergeTargetId(null);
		load();
	}

	async function handleDeleteTopic(topicId, name) {
		const ok = window.confirm(
			`Delete topic “${name}”? Prompts will move to Unassigned.`
		);
		if (!ok) return;
		setActionBusy(`delete-${topicId}`);
		const res = await deleteMonitoringTopic(websiteId, topicId);
		setActionBusy(null);
		if (!res.ok) {
			toast.error(res.message || "Could not delete topic");
			return;
		}
		toast.success("Topic deleted");
		load();
	}

	async function handleSplit(topicId) {
		const ids = (grouped.groups.find((g) => g.topicId === topicId)?.prompts || [])
			.map((p) => p.promptId)
			.filter((id) => selectedIds.has(id));
		if (!ids.length) return;
		const name = window.prompt("Name for the new topic");
		if (!name?.trim()) return;
		setActionBusy(`split-${topicId}`);
		const res = await splitMonitoringTopic(websiteId, {
			topicId,
			name: name.trim(),
			promptIds: ids,
			keywordIds: [],
		});
		setActionBusy(null);
		if (!res.ok) {
			toast.error(res.message || "Could not split selection");
			return;
		}
		toast.success("Selection split into new topic");
		clearSelection();
		load();
	}

	async function handleAutoAssign() {
		setAutoAssignBusy(true);
		const res = await autoAssignMonitoringPrompts(websiteId);
		setAutoAssignBusy(false);
		if (!res.ok) {
			toast.error(res.message || "Could not auto-assign prompts");
			return;
		}
		const n =
			res.data?.assigned_count ??
			res.data?.assignedCount ??
			grouped.unassigned.prompts.length;
		toast.success(`${n} prompts assigned to topics`);
		load();
	}

	if (loading && !list) {
		return (
			<div className="monitoring__tab-content monitoring__prompts">
				<div className="monitoring__topic-skeleton" />
				<div className="monitoring__topic-skeleton" />
				<div className="monitoring__topic-skeleton" />
			</div>
		);
	}
	if (error && !list) {
		return <p className="monitoring__empty monitoring__empty--error mb-0">{error}</p>;
	}

	const totalCount = list?.totalCount ?? 0;
	const engineOpts = filters.engines || [];
	const sourceOpts = filters.sources || [];
	const intentOpts = filters.intents || [];
	const journeyOpts = filters.journeyStages || [];
	const selectedCount = selectedIds.size;
	const topicCount = grouped.groups.length;
	const unassignedCount = grouped.unassigned.prompts.length;
	const hasFilters =
		Boolean(search) ||
		source !== "all" ||
		intent !== "all" ||
		journey !== "all" ||
		Boolean(engine);

	return (
		<div className="monitoring__tab-content monitoring__prompts">
			<div className="monitoring__prompts-toolbar">
				<div
					className="monitoring__segment"
					role="tablist"
					aria-label="Prompt status"
				>
					{STATUS_TABS.map((tab) => (
						<button
							key={tab.value}
							type="button"
							role="tab"
							aria-selected={statusFilter === tab.value}
							className={`monitoring__segment-btn${
								statusFilter === tab.value
									? " monitoring__segment-btn--active"
									: ""
							}`}
							onClick={() => setStatusFilter(tab.value)}
						>
							{tab.label}
						</button>
					))}
				</div>
			</div>

			<div className="monitoring__filter-bar monitoring__filter-bar--prompts">
				<div className="monitoring__filter-bar-left">
					<select
						className="monitoring__filter-select monitoring__filter-pill"
						value={engine}
						onChange={(e) => setEngine(e.target.value)}
						aria-label="Filter by engine"
					>
						{engineOpts.map((opt) => (
							<option key={opt.value || "all"} value={opt.value}>
								{opt.label}
							</option>
						))}
					</select>
					<select
						className="monitoring__filter-select monitoring__filter-pill"
						value={source}
						onChange={(e) => setSource(e.target.value)}
						aria-label="Filter by source"
					>
						{sourceOpts.map((opt) => (
							<option key={opt.value} value={opt.value}>
								{opt.label}
							</option>
						))}
					</select>
					<select
						className="monitoring__filter-select monitoring__filter-pill"
						value={intent}
						onChange={(e) => setIntent(e.target.value)}
						aria-label="Filter by intent"
					>
						{intentOpts.map((opt) => (
							<option key={opt.value} value={opt.value}>
								{opt.label}
							</option>
						))}
					</select>
					<select
						className="monitoring__filter-select monitoring__filter-pill"
						value={journey}
						onChange={(e) => setJourney(e.target.value)}
						aria-label="Filter by journey stage"
					>
						{journeyOpts.map((opt) => (
							<option key={opt.value} value={opt.value}>
								{opt.label}
							</option>
						))}
					</select>
					<select
						className="monitoring__filter-select monitoring__filter-pill"
						value="en-US"
						disabled
						aria-label="Locale"
						title="Multi-locale arrives later"
					>
						<option>en-US</option>
					</select>
					<div className="monitoring__filter-search-wrap">
						<Search size={14} aria-hidden />
						<input
							type="search"
							className="monitoring__filter-search"
							placeholder="Search prompts..."
							value={searchInput}
							onChange={(e) => setSearchInput(e.target.value)}
							aria-label="Search prompts"
						/>
					</div>
				</div>
				<Button
					color="primary"
					size="sm"
					className="monitoring__action-btn monitoring__action-btn--primary"
					onClick={() => setAddOpen(true)}
				>
					Add prompt
				</Button>
			</div>

			<p className="monitoring__panel-caption mb-3">
				Measured with{" "}
				{list?.webSearch
					? "web-search citations (AI discoverability)"
					: "organic citations (AI authority)"}
				.
			</p>

			{actionBanner && (
				<div className="monitoring__action-banner" role="status">
					{actionBanner}
				</div>
			)}

			{selectedCount > 0 && (
				<div className="monitoring__bulk-bar">
					<div className="monitoring__bulk-bar-left">
						<strong>{selectedCount} selected</strong>
						<span className="text-muted">
							{" "}
							 -  reassign without leaving the list
						</span>
					</div>
					<div className="monitoring__bulk-bar-right">
						<MoveToTopicDropdown
							topics={allTopics}
							value={bulkTopicId}
							onChange={setBulkTopicId}
							disabled={bulkBusy}
						/>
						<Button
							color="primary"
							size="sm"
							disabled={bulkTopicId == null || bulkBusy}
							onClick={handleBulkMove}
						>
							{bulkBusy ? <Spinner size="sm" /> : "Apply"}
						</Button>
						<Button
							color="secondary"
							outline
							size="sm"
							disabled={bulkBusy}
							onClick={clearSelection}
						>
							Clear
						</Button>
					</div>
				</div>
			)}

			{filteredRows.length === 0 ? (
				<div className="monitoring__panel monitoring__empty-panel">
					{rows.length === 0 ? (
						statusFilter === "retired" ? (
							<p className="monitoring__empty text-muted mb-0">
								No retired prompts match the filters. Prompts seed
								automatically from the baseline audit, or add one above.
							</p>
						) : statusFilter === "all" ? (
							<>
								<p className="monitoring__empty text-muted mb-2">
									No tracked prompts yet. Prompts seed automatically from the
									baseline audit, or add your first one.
								</p>
								<Button
									color="primary"
									size="sm"
									onClick={() => setAddOpen(true)}
								>
									Add your first prompt
								</Button>
							</>
						) : (
							<>
								<p className="monitoring__empty text-muted mb-2">
									No active prompts match the filters. Prompts seed
									automatically from the baseline audit, or add your first one.
								</p>
								<Button
									color="primary"
									size="sm"
									onClick={() => setAddOpen(true)}
								>
									Add your first prompt
								</Button>
							</>
						)
					) : (
						<p className="monitoring__empty text-muted mb-0">
							No prompts match your filters
							{hasFilters ? ". Try clearing filters." : "."}
						</p>
					)}
				</div>
			) : (
				<div className={`monitoring__topic-stack${loading ? " is-loading" : ""}`}>
					{grouped.groups.map((group) => {
						const selectedInGroup = group.prompts.filter((p) =>
							selectedIds.has(p.promptId)
						).length;
						const isRenaming = renameTopicId === group.topicId;
						const isMerging = mergeTopicId === group.topicId;
						const topicKey = String(group.topicId);
						const isOpen = openTopicIds.has(topicKey);

						return (
							<article
								key={group.topicId}
								className={`monitoring__topic-card${
									isOpen ? " monitoring__topic-card--open" : ""
								}${
									isRenaming || isMerging
										? " monitoring__topic-card--editing"
										: ""
								}`}
							>
								<header className="monitoring__topic-header">
									{isRenaming ? (
										<div className="monitoring__topic-header-left">
											<div className="monitoring__topic-inline-edit">
												<input
													className="monitoring__topic-rename-input"
													value={renameValue}
													onChange={(e) => setRenameValue(e.target.value)}
													aria-label="Topic name"
												/>
												<Button
													color="primary"
													size="sm"
													disabled={actionBusy === `rename-${group.topicId}`}
													onClick={() => handleRenameApply(group.topicId)}
												>
													Apply
												</Button>
												<Button
													color="secondary"
													outline
													size="sm"
													onClick={() => setRenameTopicId(null)}
												>
													Cancel
												</Button>
											</div>
										</div>
									) : (
										<button
											type="button"
											className="monitoring__topic-accordion-btn"
											aria-expanded={isOpen}
											onClick={() => toggleTopicOpen(topicKey)}
										>
											<ChevronDown
												size={16}
												className={`monitoring__topic-chevron${
													isOpen ? " is-open" : ""
												}`}
												aria-hidden
											/>
											<div className="monitoring__topic-header-left">
												<h3 className="monitoring__topic-name">
													{group.name}
												</h3>
												{group.intentPrimary && (
													<IntentChip intent={group.intentPrimary} />
												)}
												<span className="monitoring__topic-meta text-muted">
													{group.promptCount} prompt
													{group.promptCount === 1 ? "" : "s"} ·{" "}
													{group.keywordCount} keyword
													{group.keywordCount === 1 ? "" : "s"}
												</span>
											</div>
										</button>
									)}
									<div className="monitoring__topic-header-right">
										{isMerging ? (
											<div className="monitoring__topic-inline-edit">
												<span className="small text-muted">Merge into...</span>
												<MoveToTopicDropdown
													topics={allTopics}
													value={mergeTargetId}
													excludeTopicId={group.topicId}
													onChange={setMergeTargetId}
													placeholder="Merge into..."
												/>
												<Button
													color="primary"
													size="sm"
													disabled={
														mergeTargetId == null ||
														actionBusy === `merge-${group.topicId}`
													}
													onClick={() => handleMergeApply(group.topicId)}
												>
													Apply
												</Button>
												<Button
													color="secondary"
													outline
													size="sm"
													onClick={() => {
														setMergeTopicId(null);
														setMergeTargetId(null);
													}}
												>
													Cancel
												</Button>
											</div>
										) : (
											<>
												<span className="monitoring__topic-cited">
													Cited {group.citedPrompts}/
													{group.testedPrompts || group.promptCount}
												</span>
												<button
													type="button"
													className="monitoring__topic-action"
													onClick={() => {
														setRenameTopicId(group.topicId);
														setRenameValue(group.name);
														setMergeTopicId(null);
														if (!isOpen) toggleTopicOpen(topicKey);
													}}
												>
													Rename
												</button>
												<button
													type="button"
													className="monitoring__topic-action"
													onClick={() => {
														setMergeTopicId(group.topicId);
														setMergeTargetId(null);
														setRenameTopicId(null);
														if (!isOpen) toggleTopicOpen(topicKey);
													}}
												>
													Merge
												</button>
												{selectedInGroup > 0 && (
													<button
														type="button"
														className="monitoring__topic-action"
														disabled={actionBusy === `split-${group.topicId}`}
														onClick={() => handleSplit(group.topicId)}
													>
														Split selection
													</button>
												)}
												<button
													type="button"
													className="monitoring__topic-action monitoring__topic-action--danger"
													disabled={actionBusy === `delete-${group.topicId}`}
													onClick={() =>
														handleDeleteTopic(group.topicId, group.name)
													}
												>
													Delete
												</button>
											</>
										)}
									</div>
								</header>
								{isOpen && (
									<div className="monitoring__topic-body">
										<PromptsTable
											prompts={group.prompts}
											personaLabels={personaLabels}
											selectedIds={selectedIds}
											expandedId={expandedId}
											onToggleSelect={toggleSelect}
											onToggleExpand={(id) =>
												setExpandedId((prev) => (prev === id ? null : id))
											}
											onRetire={handleRetire}
											onReactivate={handleReactivate}
											onEdit={(row) => {
												setEditError(null);
												setEditRow(row);
											}}
											onAnalysis={handleAnalysis}
											onPersonas={setPersonasModalRow}
											analysingPromptId={analysingPromptId}
											websiteId={websiteId}
											onViewRaw={setRawFor}
											onToggleAll={toggleAllInGroup}
										/>
									</div>
								)}
							</article>
						);
					})}

					{unassignedCount > 0 && (
						<article
							className={`monitoring__topic-card monitoring__topic-card--unassigned${
								openTopicIds.has("unassigned")
									? " monitoring__topic-card--open"
									: ""
							}`}
						>
							<header className="monitoring__topic-header">
								<button
									type="button"
									className="monitoring__topic-accordion-btn"
									aria-expanded={openTopicIds.has("unassigned")}
									onClick={() => toggleTopicOpen("unassigned")}
								>
									<ChevronDown
										size={16}
										className={`monitoring__topic-chevron${
											openTopicIds.has("unassigned") ? " is-open" : ""
										}`}
										aria-hidden
									/>
									<div className="monitoring__topic-header-left">
										<h3 className="monitoring__topic-name">Unassigned</h3>
										<span className="monitoring__topic-meta text-muted">
											{unassignedCount} prompt
											{unassignedCount === 1 ? "" : "s"} ·{" "}
											{grouped.unassigned.keywordCount || 0} keywords
										</span>
									</div>
								</button>
								<div
									className="monitoring__topic-header-right"
									onClick={(e) => e.stopPropagation()}
								>
									<span className="monitoring__unassigned-hint text-muted">
										Assign a topic so these roll up
									</span>
									<Button
										color="secondary"
										outline
										size="sm"
										disabled={autoAssignBusy}
										onClick={handleAutoAssign}
									>
										{autoAssignBusy ? (
											<>
												<Spinner size="sm" className="me-1" />
												Assigning…
											</>
										) : (
											"Auto-assign"
										)}
									</Button>
								</div>
							</header>
							{openTopicIds.has("unassigned") && (
								<div className="monitoring__topic-body">
									<PromptsTable
										prompts={grouped.unassigned.prompts}
										personaLabels={personaLabels}
										selectedIds={selectedIds}
										expandedId={expandedId}
										onToggleSelect={toggleSelect}
										onToggleExpand={(id) =>
											setExpandedId((prev) => (prev === id ? null : id))
										}
										onRetire={handleRetire}
										onReactivate={handleReactivate}
										onEdit={(row) => {
											setEditError(null);
											setEditRow(row);
										}}
										onAnalysis={handleAnalysis}
										onPersonas={setPersonasModalRow}
										analysingPromptId={analysingPromptId}
										websiteId={websiteId}
										onViewRaw={setRawFor}
										onToggleAll={toggleAllInGroup}
									/>
								</div>
							)}
						</article>
					)}
				</div>
			)}

			<p className="monitoring__prompts-footer text-muted">
				{totalCount} tracked prompts across {topicCount} topic
				{topicCount === 1 ? "" : "s"} · {unassignedCount} unassigned. Retired
				prompts stay queryable for history and are never re-run; the library is
				never regenerated (prompt continuity).
			</p>

			<AddPromptModal
				isOpen={addOpen}
				onClose={() => {
					if (!addSubmitBusy) setAddOpen(false);
				}}
				onSubmitOne={submitAddPrompt}
				onCompleted={handlePromptsAdded}
				onBusyChange={setAddSubmitBusy}
				topics={allTopics}
				websiteId={websiteId}
				maxPersonas={maxPersonasPerPrompt}
				examplePrompt={examplePrompt}
			/>

			<PromptPersonasModal
				isOpen={personasModalRow != null}
				onClose={() => setPersonasModalRow(null)}
				onSaved={(message) => {
					toast.success(message);
					load();
				}}
				websiteId={websiteId}
				promptRow={personasModalRow}
				maxPersonas={maxPersonasPerPrompt}
			/>

			<EditClassificationModal
				isOpen={Boolean(editRow)}
				onClose={() => !editBusy && setEditRow(null)}
				onSubmit={handleEditSave}
				busy={editBusy}
				topics={allTopics}
				row={editRow}
				error={editError}
			/>

			{rawFor && (
				<RawAnswersModal
					websiteId={websiteId}
					promptId={rawFor.promptId}
					promptText={rawFor.prompt}
					onClose={() => setRawFor(null)}
				/>
			)}
		</div>
	);
}
